"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import type { ApiDropPart } from "@/generated/models/ApiDropPart";
import WaveDropPartContentMedias from "./WaveDropPartContentMedias";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiDropGroupMention } from "@/generated/models/ApiDropGroupMention";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import type { ReferencedNft } from "@/entities/IDrop";
import type { ApiWaveMin } from "@/generated/models/ApiWaveMin";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import WaveDropPartContentMarkdown from "./WaveDropPartContentMarkdown";
import WaveDropPartContentAttachments from "./WaveDropPartContentAttachments";
import { ImageScale } from "@/helpers/image.helpers";
import type { DropContentPresentation } from "./dropContentPresentation";
interface WaveDropPartContentProps {
  readonly mentionedUsers: ApiDropMentionedUser[];
  readonly mentionedGroups?: ApiDropGroupMention[] | undefined;
  readonly mentionedWaves: ApiMentionedWave[];
  readonly referencedNfts: ReferencedNft[];
  readonly wave: ApiWaveMin;
  readonly activePart: ApiDropPart;
  readonly havePreviousPart: boolean;
  readonly haveNextPart: boolean;
  readonly isStorm: boolean;
  readonly activePartIndex: number;
  readonly setActivePartIndex: (index: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isEditing?: boolean | undefined;
  readonly isSaving?: boolean | undefined;
  readonly onSave?: ((newContent: string, mentions?: ApiDropMentionedUser[], mentionedGroups?: ApiDropGroupMention[], mentionedWaves?: ApiMentionedWave[]) => void) | undefined;
  readonly onCancel?: (() => void) | undefined;
  readonly drop?: ApiDrop | undefined;
  readonly isCompetitionDrop?: boolean | undefined;
  readonly mediaImageScale?: ImageScale | undefined;
  readonly fullWidthMedia?: boolean | undefined;
  readonly onLinkCardActionsActiveChange?: ((href: string, active: boolean) => void) | undefined;
  readonly contentPresentation?: DropContentPresentation | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
const WaveDropPartContent: React.FC<WaveDropPartContentProps> = t0 => {
  const $ = _c(53);
  const {
    mentionedUsers,
    mentionedGroups: t1,
    mentionedWaves,
    referencedNfts,
    wave,
    activePart,
    havePreviousPart,
    haveNextPart,
    isStorm,
    activePartIndex,
    setActivePartIndex,
    onQuoteClick,
    isEditing: t2,
    isSaving: t3,
    onSave,
    onCancel,
    drop,
    isCompetitionDrop: t4,
    mediaImageScale: t5,
    fullWidthMedia: t6,
    onLinkCardActionsActiveChange,
    contentPresentation: t7,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  let t8;
  if ($[0] !== t1) {
    t8 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t8;
  } else {
    t8 = $[1];
  }
  const mentionedGroups = t8;
  const isEditing = t2 === undefined ? false : t2;
  const isSaving = t3 === undefined ? false : t3;
  const isCompetitionDrop = t4 === undefined ? false : t4;
  const mediaImageScale = t5 === undefined ? ImageScale.AUTOx450 : t5;
  const fullWidthMedia = t6 === undefined ? false : t6;
  const contentPresentation = t7 === undefined ? "default" : t7;
  const contentRef = React.useRef(null);
  const memoizedMentionedUsers = mentionedUsers;
  const memoizedMentionedWaves = mentionedWaves;
  const memoizedMentionedGroups = mentionedGroups;
  const memoizedReferencedNfts = referencedNfts;
  let t9;
  if ($[2] !== activePartIndex || $[3] !== haveNextPart || $[4] !== havePreviousPart || $[5] !== setActivePartIndex) {
    t9 = direction => {
      const isPrevious = direction === "previous";
      const isDisabled = isPrevious ? !havePreviousPart : !haveNextPart;
      const onClick = () => setActivePartIndex(activePartIndex + (isPrevious ? -1 : 1));
      return <button disabled={isDisabled} className={`${isDisabled ? "tw-cursor-default tw-border-iron-700 tw-text-iron-700" : "tw-border-primary-400 tw-text-primary-400 hover:tw-bg-primary-400 hover:tw-text-white"} tw-flex tw-size-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-bg-transparent tw-transition tw-duration-300 tw-ease-out sm:tw-size-6`} onClick={e => {
        e.stopPropagation();
        onClick();
      }} aria-label={`${isPrevious ? "Previous" : "Next"} part`}><svg className="tw-size-5 tw-flex-shrink-0 sm:tw-size-4" xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d={isPrevious ? "M15.75 19.5 8.25 12l7.5-7.5" : "m8.25 4.5 7.5 7.5-7.5 7.5"} /></svg></button>;
    };
    $[2] = activePartIndex;
    $[3] = haveNextPart;
    $[4] = havePreviousPart;
    $[5] = setActivePartIndex;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const renderNavigationButton = t9;
  let t10;
  if ($[7] !== isStorm || $[8] !== renderNavigationButton) {
    t10 = isStorm && <div className="tw-mb-3 tw-flex tw-justify-between tw-space-x-3 md:tw-hidden">{renderNavigationButton("previous")}{renderNavigationButton("next")}</div>;
    $[7] = isStorm;
    $[8] = renderNavigationButton;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== isStorm || $[11] !== renderNavigationButton) {
    t11 = isStorm && <div className="tw-hidden md:tw-block">{renderNavigationButton("previous")}</div>;
    $[10] = isStorm;
    $[11] = renderNavigationButton;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== activePart || $[14] !== contentPresentation || $[15] !== drop || $[16] !== embedDepth || $[17] !== embedPath || $[18] !== isEditing || $[19] !== isSaving || $[20] !== maxEmbedDepth || $[21] !== memoizedMentionedGroups || $[22] !== memoizedMentionedUsers || $[23] !== memoizedMentionedWaves || $[24] !== memoizedReferencedNfts || $[25] !== onCancel || $[26] !== onLinkCardActionsActiveChange || $[27] !== onQuoteClick || $[28] !== onSave || $[29] !== quotePath || $[30] !== wave) {
    t12 = <div><WaveDropPartContentMarkdown mentionedUsers={memoizedMentionedUsers} mentionedGroups={memoizedMentionedGroups} mentionedWaves={memoizedMentionedWaves} referencedNfts={memoizedReferencedNfts} part={activePart} wave={wave} onQuoteClick={onQuoteClick} isEditing={isEditing} isSaving={isSaving} onSave={onSave} onCancel={onCancel} drop={drop} onLinkCardActionsActiveChange={onLinkCardActionsActiveChange} contentPresentation={contentPresentation} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} /></div>;
    $[13] = activePart;
    $[14] = contentPresentation;
    $[15] = drop;
    $[16] = embedDepth;
    $[17] = embedPath;
    $[18] = isEditing;
    $[19] = isSaving;
    $[20] = maxEmbedDepth;
    $[21] = memoizedMentionedGroups;
    $[22] = memoizedMentionedUsers;
    $[23] = memoizedMentionedWaves;
    $[24] = memoizedReferencedNfts;
    $[25] = onCancel;
    $[26] = onLinkCardActionsActiveChange;
    $[27] = onQuoteClick;
    $[28] = onSave;
    $[29] = quotePath;
    $[30] = wave;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== activePart || $[33] !== fullWidthMedia || $[34] !== isCompetitionDrop || $[35] !== mediaImageScale) {
    t13 = !!activePart.media.length && <WaveDropPartContentMedias activePart={activePart} isCompetitionDrop={isCompetitionDrop} imageScale={mediaImageScale} fullWidthMedia={fullWidthMedia} />;
    $[32] = activePart;
    $[33] = fullWidthMedia;
    $[34] = isCompetitionDrop;
    $[35] = mediaImageScale;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== activePart.attachments) {
    t14 = activePart.attachments ?? [];
    $[37] = activePart.attachments;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t14) {
    t15 = <WaveDropPartContentAttachments attachments={t14} />;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t12 || $[42] !== t13 || $[43] !== t15) {
    t16 = <div className="tw-h-full tw-w-full" ref={contentRef}>{t12}{t13}{t15}</div>;
    $[41] = t12;
    $[42] = t13;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== isStorm || $[46] !== renderNavigationButton) {
    t17 = isStorm && <div className="tw-hidden md:tw-block">{renderNavigationButton("next")}</div>;
    $[45] = isStorm;
    $[46] = renderNavigationButton;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t10 || $[49] !== t11 || $[50] !== t16 || $[51] !== t17) {
    t18 = <div className="tw-w-full"><div className="tw-flex tw-w-full tw-flex-col tw-justify-between md:tw-flex-row md:tw-gap-x-3">{t10}{t11}{t16}{t17}</div></div>;
    $[48] = t10;
    $[49] = t11;
    $[50] = t16;
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  return t18;
};
export default WaveDropPartContent;